import React, { useState, useMemo } from "react"
import { motion } from "framer-motion"
import {DollarSign, CheckCircle, Clock, Trash2, Eye, Download, User, QrCode, AlertTriangle, Printer, FileQuestion} from 'lucide-react'
import { useGuias } from "../context/GuiasContext"
import toast from "react-hot-toast"
import QRCodeLib from "qrcode"
import { gerarPixPayload } from "../utils/pixQrCode"
import html2pdf from "html2pdf.js"

export default function PagamentosGerados() {
  const { pagamentos, deletePagamento, marcarComoPago, marcarComoSemComprovante, dadosPrefeitura } = useGuias()
  const [pagamentoSelecionado, setPagamentoSelecionado] = useState<any>(null)
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>("")
  const [processandoPagamento, setProcessandoPagamento] = useState<string | null>(null)
  const [pagamentosProcessados, setPagamentosProcessados] = useState<Set<string>>(new Set())

  // Filtrar apenas pagamentos pendentes e NÃO expirados
  const pagamentosPendentes = useMemo(() => {
    const agora = new Date()
    return pagamentos.filter(p => {
      if (p.statusPagamento !== "pendente") return false
      
      // Se não tem data de expiração, mostrar
      if (!p.dataExpiracao) return true
      
      // Se tem data de expiração, só mostrar se NÃO está expirado
      const expiracao = new Date(p.dataExpiracao)
      return expiracao.getTime() > agora.getTime()
    })
  }, [pagamentos])

  // Calcular total de pagamentos pendentes
  const totalPendente = useMemo(() => {
    return pagamentosPendentes.reduce((acc, p) => {
      const valor = parseFloat(p.valor.toString().replace(',', '.'))
      return acc + valor
    }, 0)
  }, [pagamentosPendentes])

  const handleDeletar = async (id: string, numeroComprovante: string) => {
    if (window.confirm(`Deseja realmente excluir o pagamento ${numeroComprovante}?`)) {
      await deletePagamento(id)
    }
  }

  const handleVisualizarComprovante = async (pagamento: any) => {
    setPagamentoSelecionado(pagamento)
    
    // Gerar QR Code
    const pixPayload = gerarPixPayload(
      dadosPrefeitura?.chavePix || "",
      (dadosPrefeitura?.nomePrefeitura || "Prefeitura").substring(0, 25),
      "Sao Jose do Serido",
      pagamento.valor,
      pagamento.numeroComprovante
    )
    
    try {
      const qrCodeUrl = await QRCodeLib.toDataURL(pixPayload, {
        width: 180,
        margin: 1,
        color: {
          dark: "#000000",
          light: "#FFFFFF"
        }
      })
      setQrCodeDataUrl(qrCodeUrl)
    } catch (error) {
      console.error("Erro ao gerar QR Code:", error)
      toast.error("Erro ao gerar QR Code")
    }
  }

  const handleImprimirComprovante = () => {
    window.print()
  }

  const handleFecharComprovante = () => {
    setPagamentoSelecionado(null)
    setQrCodeDataUrl("")
  }
  
  const handleMarcarComoPago = async (pagamento: any) => {
    // PROTEÇÃO 1: Verificar se já está processando este pagamento
    if (processandoPagamento === pagamento._id) {
      toast.error("⏳ Aguarde! Processando pagamento...")
      return
    }
    
    // PROTEÇÃO 2: Verificar se este pagamento já foi processado (prevenir race condition)
    if (pagamentosProcessados.has(pagamento._id)) {
      toast.error("🚫 Este PIX já está sendo processado ou foi pago!")
      return
    }
    
    // PROTEÇÃO 3: Verificar status no objeto
    if (pagamento.statusPagamento === "pago") {
      toast.error("✓ Este PIX já foi pago e não pode ser pago novamente!")
      return
    }
    
    // PROTEÇÃO 4: Verificar se está expirado
    if (pagamento.dataExpiracao) {
      const agora = new Date()
      const expiracao = new Date(pagamento.dataExpiracao)
      if (expiracao.getTime() <= agora.getTime()) {
        toast.error("⏰ Este PIX está expirado. Gere um novo pagamento.")
        return
      }
    }
    
    // BLOQUEAR IMEDIATAMENTE - adicionar ao Set de processados
    setPagamentosProcessados(prev => new Set(prev).add(pagamento._id!))
    setProcessandoPagamento(pagamento._id!)
    
    try {
      await marcarComoPago(pagamento._id!)
      toast.success("✓ Pagamento confirmado com sucesso!")
    } catch (error) {
      console.error("Erro ao marcar pagamento:", error)
      // Remover do Set em caso de erro
      setPagamentosProcessados(prev => {
        const newSet = new Set(prev)
        newSet.delete(pagamento._id!)
        return newSet
      })
      toast.error("❌ Erro ao confirmar pagamento. Tente novamente.")
    } finally {
      setProcessandoPagamento(null)
    }
  }

  const handleMarcarComoSemComprovante = async (pagamento: any) => {
    if (processandoPagamento === pagamento._id) {
      toast.error("Aguarde! Processando...")
      return
    }
    
    if (pagamento.statusPagamento === "pago") {
      toast.error("Este pagamento já foi confirmado!")
      return
    }
    
    setProcessandoPagamento(pagamento._id!)
    
    try {
      await marcarComoSemComprovante(pagamento._id!)
    } catch (error) {
      console.error("Erro ao marcar como sem comprovante:", error)
    } finally {
      setProcessandoPagamento(null)
    }
  }
  
  const verificarExpiracao = (dataExpiracao?: string) => {
    if (!dataExpiracao) return { expirado: false, label: "Sem validade" }
    
    const agora = new Date()
    const expiracao = new Date(dataExpiracao)
    const diff = expiracao.getTime() - agora.getTime()
    
    if (diff <= 0) {
      return { expirado: true, label: "EXPIRADO" }
    }
    
    const horas = Math.floor(diff / (1000 * 60 * 60))
    const minutos = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    
    return { expirado: false, label: `${horas}h ${minutos}m` }
  }

  const handleImprimirNovaAba = async (pagamento: any) => {
    try {
      // Gerar QR Code
      const pixPayload = gerarPixPayload(
        dadosPrefeitura?.chavePix || "",
        (dadosPrefeitura?.nomePrefeitura || "Prefeitura").substring(0, 25),
        "Sao Jose do Serido",
        pagamento.valor,
        pagamento.numeroComprovante
      )
      
      const qrCodeUrl = await QRCodeLib.toDataURL(pixPayload, {
        width: 180,
        margin: 1,
        color: {
          dark: "#000000",
          light: "#FFFFFF"
        }
      })

      // Calcular tempo restante
      let statusHtml = ""
      if (pagamento.statusPagamento === "pago") {
        statusHtml = `
          <div class="status-expirado">
            <p>✓ PAGAMENTO CONFIRMADO</p>
            <p style="font-size: 10px;">Pago em: ${pagamento.dataPagamento ? new Date(pagamento.dataPagamento).toLocaleString("pt-BR") : "N/A"}</p>
          </div>
        `
      } else if (pagamento.dataExpiracao) {
        const agora = new Date()
        const expiracao = new Date(pagamento.dataExpiracao)
        const diff = expiracao.getTime() - agora.getTime()
        
        if (diff <= 0) {
          statusHtml = `
            <div class="status-expirado">
              <p>⚠ QR CODE EXPIRADO</p>
              <p style="font-size: 10px;">Este pagamento não é mais válido. Gere um novo talão.</p>
            </div>
          `
        } else {
          statusHtml = `
            <div class="status-valido">
              <p>✓ QR CODE VÁLIDO</p>
              <p style="font-size: 10px;">Vencimento: ${new Date(pagamento.dataExpiracao).toLocaleDateString("pt-BR")} - Válido até 23:59:59</p>
            </div>
          `
        }
      }

      // Abrir nova aba com o comprovante
      const novaAba = window.open("", "_blank")
      if (!novaAba) {
        toast.error("Bloqueador de pop-up ativo. Permita pop-ups para imprimir.")
        return
      }

      novaAba.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Talão PIX ${pagamento.numeroComprovante}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 20px; background: #fff; }
            .talao { max-width: 800px; margin: 0 auto; background: white; border: 2px solid #333; }
            
            /* Cabeçalho Institucional */
            .cabecalho { display: flex; align-items: center; padding: 20px; border-bottom: 3px solid #333; }
            .logo-prefeitura { width: 100px; height: 100px; margin-right: 20px; flex-shrink: 0; }
            .dados-prefeitura { flex: 1; }
            .dados-prefeitura h1 { font-size: 20px; font-weight: bold; color: #004161; margin-bottom: 8px; text-transform: uppercase; border-bottom: 2px solid #004161; padding-bottom: 4px; }
            .dados-prefeitura p { font-size: 11px; color: #333; margin: 2px 0; line-height: 1.4; }
            
            /* Título do Talão */
            .titulo-talao { background: #d0d0d0; padding: 12px; text-align: center; border-bottom: 2px solid #333; }
            .titulo-talao h2 { font-size: 18px; font-weight: bold; color: #333; letter-spacing: 1px; }
            
            /* Conteúdo em 2 colunas */
            .conteudo { display: grid; grid-template-columns: 1fr 1fr; }
            
            /* Coluna Esquerda - Dados do Pagador */
            .coluna-esquerda { padding: 20px; border-right: 2px solid #333; }
            .coluna-esquerda h3 { font-size: 14px; font-weight: bold; color: #333; margin-bottom: 15px; text-transform: uppercase; }
            .campo { margin-bottom: 12px; }
            .campo-label { font-size: 11px; font-weight: bold; color: #333; margin-bottom: 2px; }
            .campo-valor { font-size: 12px; color: #000; }
            
            /* Coluna Direita - Pagamento via PIX */
            .coluna-direita { padding: 20px; display: flex; flex-direction: column; align-items: center; justify-content: center; }
            .coluna-direita h3 { font-size: 14px; font-weight: bold; color: #333; margin-bottom: 15px; text-transform: uppercase; }
            .qr-code-box { border: 3px solid #333; padding: 10px; background: white; margin-bottom: 15px; }
            .qr-code-box img { display: block; width: 200px; height: 200px; }
            .info-pagamento { width: 100%; }
            .info-pagamento .campo { margin-bottom: 8px; text-align: center; }
            .info-pagamento .campo-label { font-size: 11px; font-weight: bold; color: #333; }
            .info-pagamento .campo-valor { font-size: 13px; color: #000; font-weight: 600; }
            
            /* Rodapé */
            .rodape { padding: 12px 20px; border-top: 2px solid #333; text-align: center; background: #f5f5f5; }
            .rodape p { font-size: 10px; color: #333; margin: 2px 0; }
            
            .status-expirado { background: #ffcccc; border: 2px solid #cc0000; padding: 10px; margin: 10px 20px; text-align: center; }
            .status-expirado p { color: #cc0000; font-weight: bold; font-size: 12px; margin: 4px 0; }
            
            .status-valido { background: #ccffcc; border: 2px solid #00cc00; padding: 10px; margin: 10px 20px; text-align: center; }
            .status-valido p { color: #006600; font-weight: bold; font-size: 12px; margin: 4px 0; }
            
            @media print {
              body { padding: 0; background: white; }
              .talao { border: 2px solid #000; }
              @page { margin: 10mm; }
            }
          </style>
        </head>
        <body>
          <div class="talao">
            <!-- Cabeçalho com Logo e Dados da Prefeitura -->
            <div class="cabecalho">
              <img src="${dadosPrefeitura?.logoUrl || 'https://lumi.new/lumi.ing/logo.png'}" alt="Logo" class="logo-prefeitura" />
              <div class="dados-prefeitura">
                <h1>${dadosPrefeitura?.nomePrefeitura || 'Prefeitura Municipal'}</h1>
                <p><strong>CNPJ:</strong> ${dadosPrefeitura?.cnpj || 'Não informado'}</p>
                <p><strong>Endereço:</strong> ${dadosPrefeitura?.endereco || 'Não informado'}</p>
                <p><strong>CEP:</strong> ${dadosPrefeitura?.cep || 'Não informado'} | <strong>Tel:</strong> ${dadosPrefeitura?.telefone || 'Não informado'}</p>
                <p><strong>Site:</strong> https://saojosedoserido.rn.gov.br/</p>
              </div>
            </div>
            
            <!-- Título do Talão -->
            <div class="titulo-talao">
              <h2>TALÃO DE PAGAMENTO PIX</h2>
            </div>
            
            <!-- Status de Validade -->
            ${statusHtml}
            
            <!-- Conteúdo em 2 Colunas -->
            <div class="conteudo">
              <!-- Coluna Esquerda - Dados do Pagador -->
              <div class="coluna-esquerda">
                <h3>Dados do Pagador</h3>
                <div class="campo">
                  <div class="campo-label">Nome:</div>
                  <div class="campo-valor">${pagamento.nomePagador}</div>
                </div>
                <div class="campo">
                  <div class="campo-label">CPF:</div>
                  <div class="campo-valor">${pagamento.cpfPagador}</div>
                </div>
                <div class="campo">
                  <div class="campo-label">Referente a:</div>
                  <div class="campo-valor">${pagamento.referenciaPagamento}</div>
                </div>
                <div class="campo">
                  <div class="campo-label">Número do Comprovante:</div>
                  <div class="campo-valor">${pagamento.numeroComprovante}</div>
                </div>
                <div class="campo">
                  <div class="campo-label">Data de Emissão:</div>
                  <div class="campo-valor">${new Date(pagamento.createdAt).toLocaleDateString("pt-BR")} às ${new Date(pagamento.createdAt).toLocaleTimeString("pt-BR")}</div>
                </div>
                ${pagamento.isNFSe ? `
                <div class="campo">
                  <div class="campo-label">NFS-e - Nota Fiscal Nº:</div>
                  <div class="campo-valor" style="font-weight: bold; color: #004161;">${pagamento.numeroNotaFiscal}</div>
                </div>
                ` : ''}
                ${pagamento.isTLL ? `
                <div class="campo">
                  <div class="campo-label">Taxa TLL:</div>
                  <div class="campo-valor" style="font-weight: bold; color: #004161;">R$ ${pagamento.valorTLL}</div>
                </div>
                ` : ''}
                ${pagamento.isTFF ? `
                <div class="campo">
                  <div class="campo-label">Taxa TFF:</div>
                  <div class="campo-valor" style="font-weight: bold; color: #004161;">R$ ${pagamento.valorTFF}</div>
                </div>
                ` : ''}
              </div>
              
              <!-- Coluna Direita - Pagamento via PIX -->
              <div class="coluna-direita">
                <h3>Pagamento via PIX</h3>
                <div class="qr-code-box">
                  <img src="${qrCodeUrl}" alt="QR Code PIX" />
                </div>
                <div class="info-pagamento">
                  <div class="campo">
                    <div class="campo-label">CHAVE PIX:</div>
                    <div class="campo-valor" style="font-size: 10px; word-break: break-all;">${pagamento.chavePix}</div>
                  </div>
                  <div class="campo">
                    <div class="campo-label">VALOR:</div>
                    <div class="campo-valor" style="font-size: 18px; color: #004161;">R$ ${pagamento.valor}</div>
                  </div>
                  <div class="campo">
                    <div class="campo-label">VENCIMENTO:</div>
                    <div class="campo-valor">${pagamento.dataExpiracao ? new Date(pagamento.dataExpiracao).toLocaleDateString("pt-BR") : "Sem vencimento"}</div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Código PIX Copia e Cola -->
            <div style="padding: 12px 20px; border-top: 2px solid #333; background: #fff;">
              <p style="font-size: 9px; color: #333; font-weight: bold; margin: 0 0 6px 0; text-align: center; text-transform: uppercase;">Código PIX Copia e Cola</p>
              <div style="background: #f5f5f5; border: 1px dashed #999; border-radius: 4px; padding: 6px; margin-bottom: 8px;">
                <p style="font-size: 7px; font-family: monospace; color: #333; margin: 0; word-break: break-all; line-height: 1.3;">${pixPayload}</p>
              </div>
            </div>
            
            <!-- Rodapé -->
            <div class="rodape">
              <p><strong>Escaneie o QR Code acima com o aplicativo do seu banco para realizar o pagamento via PIX.</strong></p>
              <p>Este talão comprova a geração do pagamento. Para confirmação, envie comprovante para 84 98159-6466</p>
              <p>Código de Autenticidade: ${pagamento.numeroComprovante} | Sistema EMIT - ${dadosPrefeitura?.nomePrefeitura || 'Prefeitura'}</p>
            </div>
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); }, 500);
            }
          </script>
        </body>
        </html>
      `)
      novaAba.document.close()
    } catch (error) {
      console.error("Erro ao gerar comprovante:", error)
      toast.error("Erro ao gerar comprovante para impressão")
    }
  }

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-gray-200"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-amber-500 rounded-lg">
            <DollarSign className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-gray-800">Pagamentos Gerados</h3>
            <p className="text-xs sm:text-sm text-gray-600">Pagamentos pendentes aguardando confirmação</p>
          </div>
        </div>

        {/* Card de Totalizador */}
        {pagamentosPendentes.length > 0 && (
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-2 sm:p-3 bg-amber-500 rounded-full flex-shrink-0">
                  <DollarSign className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-600 font-medium">Total Pendente</p>
                  <p className="text-xs text-gray-500">{pagamentosPendentes.length} pagamento{pagamentosPendentes.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <div className="text-left sm:text-right w-full sm:w-auto">
                <p className="text-xs text-gray-500 uppercase mb-1">Valor</p>
                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-amber-600">R$ {totalPendente.toFixed(2).replace('.', ',')}</p>
              </div>
            </div>
          </div>
        )}

        {pagamentosPendentes.length === 0 ? (
          <div className="text-center py-8 sm:py-12 px-3">
            <DollarSign className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-3 sm:mb-4" />
            <p className="text-gray-500 text-sm sm:text-base md:text-lg">Nenhum pagamento pendente</p>
            <p className="text-gray-400 text-xs sm:text-sm mt-2">Pagamentos pendentes aparecerão aqui</p>
          </div>
        ) : (
          <>
            {/* Tabela Desktop */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Comprovante</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Pagador</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">CPF</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Referência</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Valor</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Validade</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Data</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {pagamentosPendentes.map((pagamento, index) => {
                    const statusValidade = verificarExpiracao(pagamento.dataExpiracao)
                    
                    return (
                    <motion.tr
                      key={pagamento._id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-4 text-sm text-gray-800 font-medium">{pagamento.numeroComprovante}</td>
                      <td className="px-4 py-4 text-sm text-gray-800">{pagamento.nomePagador}</td>
                      <td className="px-4 py-4 text-sm text-gray-600">{pagamento.cpfPagador}</td>
                      <td className="px-4 py-4 text-sm text-gray-600">{pagamento.referenciaPagamento}</td>
                      <td className="px-4 py-4 text-sm text-gray-800 font-semibold">R$ {pagamento.valor}</td>
                      <td className="px-4 py-4 text-sm">
                        {pagamento.dataExpiracao ? (
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                            statusValidade.expirado 
                              ? "bg-red-100 text-red-700" 
                              : "bg-green-100 text-green-700"
                          }`}>
                            {statusValidade.expirado ? (
                              <AlertTriangle className="w-3 h-3" />
                            ) : (
                              <Clock className="w-3 h-3" />
                            )}
                            {statusValidade.label}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">Sem validade</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        {pagamento.statusPagamento === "pago" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                            <CheckCircle className="w-3 h-3" />
                            PAGO
                          </span>
                        ) : statusValidade.expirado ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                            <AlertTriangle className="w-3 h-3" />
                            VENCIDO
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
                            <Clock className="w-3 h-3" />
                            PENDENTE
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {new Date(pagamento.createdAt).toLocaleDateString("pt-BR")}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center gap-2 flex-wrap">
                          <button
                            onClick={() => handleVisualizarComprovante(pagamento)}
                            className="flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 transition-colors text-xs"
                          >
                            <Eye className="w-3 h-3" />
                            Ver
                          </button>
                          <button
                            onClick={() => handleImprimirNovaAba(pagamento)}
                            className="flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-xs"
                          >
                            <Printer className="w-3 h-3" />
                          </button>
                          {pagamento.statusPagamento === "pendente" && !verificarExpiracao(pagamento.dataExpiracao).expirado && (
                            <>
                              <button
                                onClick={() => handleMarcarComoPago(pagamento)}
                                disabled={processandoPagamento !== null || pagamentosProcessados.has(pagamento._id)}
                                className={`flex items-center gap-1 px-2 py-1 rounded-lg transition-colors text-xs ${
                                  processandoPagamento !== null || pagamentosProcessados.has(pagamento._id)
                                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                                }`}
                              >
                                <CheckCircle className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => handleMarcarComoSemComprovante(pagamento)}
                                disabled={processandoPagamento !== null || pagamentosProcessados.has(pagamento._id)}
                                className={`flex items-center gap-1 px-2 py-1 rounded-lg transition-colors text-xs ${
                                  processandoPagamento !== null || pagamentosProcessados.has(pagamento._id)
                                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    : "bg-orange-100 text-orange-700 hover:bg-orange-200"
                                }`}
                              >
                                <FileQuestion className="w-3 h-3" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleDeletar(pagamento._id!, pagamento.numeroComprovante)}
                            className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-xs"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Cards Mobile/Tablet */}
            <div className="lg:hidden space-y-4">
              {pagamentosPendentes.map((pagamento, index) => {
                const statusValidade = verificarExpiracao(pagamento.dataExpiracao)
                
                return (
                  <motion.div
                    key={pagamento._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm"
                  >
                    {/* Cabeçalho do Card */}
                    <div className="flex justify-between items-start mb-2 pb-2 border-b border-gray-200">
                      <div>
                        <p className="text-xs text-gray-500 uppercase mb-0.5">Nº</p>
                        <p className="text-sm font-bold text-gray-800">{pagamento.numeroComprovante}</p>
                      </div>
                      <div>
                        {pagamento.statusPagamento === "pago" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                            <CheckCircle className="w-3 h-3" />
                            PAGO
                          </span>
                        ) : statusValidade.expirado ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                            <AlertTriangle className="w-3 h-3" />
                            VENCIDO
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
                            <Clock className="w-3 h-3" />
                            PENDENTE
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Informações do Pagamento */}
                    <div className="space-y-1.5 mb-2">
                      <div>
                        <p className="text-xs text-gray-500">Pagador</p>
                        <p className="text-sm font-medium text-gray-800 truncate">{pagamento.nomePagador}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <p className="text-xs text-gray-500">CPF</p>
                          <p className="text-xs text-gray-700">{pagamento.cpfPagador}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Valor</p>
                          <p className="text-sm font-bold text-gray-800">R$ {pagamento.valor}</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Ref.</p>
                        <p className="text-xs text-gray-700 line-clamp-2">{pagamento.referenciaPagamento}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <p className="text-xs text-gray-500">Data</p>
                          <p className="text-xs text-gray-700">{new Date(pagamento.createdAt).toLocaleDateString("pt-BR")}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Validade</p>
                          {pagamento.dataExpiracao ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                              statusValidade.expirado 
                                ? "bg-red-100 text-red-700" 
                                : "bg-green-100 text-green-700"
                            }`}>
                              {statusValidade.expirado ? (
                                <AlertTriangle className="w-3 h-3" />
                              ) : (
                                <Clock className="w-3 h-3" />
                              )}
                              {statusValidade.label}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">Sem validade</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Botões de Ação */}
                    <div className="flex flex-wrap gap-1.5 pt-2 border-t border-gray-200">
                      <button
                        onClick={() => handleVisualizarComprovante(pagamento)}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 transition-colors text-xs font-medium flex-1 justify-center min-w-[80px]"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        Ver
                      </button>
                      <button
                        onClick={() => handleImprimirNovaAba(pagamento)}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-xs font-medium flex-1 justify-center min-w-[80px]"
                      >
                        <Printer className="w-3.5 h-3.5" />
                        <span className="hidden xs:inline">Imprimir</span>
                      </button>
                      {pagamento.statusPagamento === "pendente" && !verificarExpiracao(pagamento.dataExpiracao).expirado && (
                        <>
                          <button
                            onClick={() => handleMarcarComoPago(pagamento)}
                            disabled={processandoPagamento !== null || pagamentosProcessados.has(pagamento._id)}
                            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg transition-colors text-xs font-medium flex-1 justify-center min-w-[80px] ${
                              processandoPagamento !== null || pagamentosProcessados.has(pagamento._id)
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                            }`}
                          >
                            <CheckCircle className="w-3.5 h-3.5" />
                            Pagar
                          </button>
                          <button
                            onClick={() => handleMarcarComoSemComprovante(pagamento)}
                            disabled={processandoPagamento !== null || pagamentosProcessados.has(pagamento._id)}
                            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg transition-colors text-xs font-medium w-full justify-center ${
                              processandoPagamento !== null || pagamentosProcessados.has(pagamento._id)
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-orange-100 text-orange-700 hover:bg-orange-200"
                            }`}
                          >
                            <FileQuestion className="w-3.5 h-3.5" />
                            <span className="hidden xs:inline">Sem Comprovante</span>
                            <span className="xs:hidden">S/Comp.</span>
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDeletar(pagamento._id!, pagamento.numeroComprovante)}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-xs font-medium w-full justify-center"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Excluir
                      </button>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </>
        )}
      </motion.div>

      {/* Modal de Visualização do Comprovante */}
      {pagamentoSelecionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={handleFecharComprovante}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Cabeçalho do Modal */}
            <div className="sticky top-0 bg-gradient-to-r from-[#004161] to-[#005580] p-4 rounded-t-xl flex justify-between items-center">
              <h3 className="text-lg font-bold text-white">Visualizar Comprovante</h3>
              <button
                onClick={handleFecharComprovante}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Conteúdo do Modal */}
            <div className="p-6">
              {/* Comprovante estilo bancário */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 shadow-2xl">
                {/* Cabeçalho bancário */}
                <div className="bg-gradient-to-r from-[#004161] to-[#005580] rounded-t-lg p-4 -mx-6 -mt-6 mb-4">
                  <div className="text-center text-white">
                    <h2 className="text-xl font-bold mb-1">COMPROVANTE DE PAGAMENTO PIX</h2>
                    <p className="text-sm opacity-90">Sistema de Arrecadação Municipal</p>
                  </div>
                </div>

                {/* Status de Validade */}
                {(() => {
                  const statusValidade = verificarExpiracao(pagamentoSelecionado.dataExpiracao)
                  return statusValidade.expirado ? (
                    <div className="bg-red-100 border-2 border-red-500 rounded-lg p-4 mb-4">
                      <div className="flex items-center justify-center gap-2">
                        <AlertTriangle className="w-6 h-6 text-red-600" />
                        <p className="text-red-800 font-bold uppercase">QR CODE EXPIRADO</p>
                      </div>
                      <p className="text-sm text-red-700 text-center mt-2">Este pagamento não é mais válido.</p>
                    </div>
                  ) : (
                    <div className="bg-green-100 border-2 border-green-500 rounded-lg p-4 mb-4">
                      <div className="text-center">
                        <p className="text-green-800 font-bold text-lg mb-2">✓ QR CODE VÁLIDO</p>
                        <p className="text-green-900 font-semibold text-lg">
                          📅 Vencimento: {pagamentoSelecionado.dataExpiracao ? new Date(pagamentoSelecionado.dataExpiracao).toLocaleDateString("pt-BR") : "Sem vencimento"}
                        </p>
                        <p className="text-sm text-green-700 mt-1">Tempo restante: {statusValidade.label}</p>
                      </div>
                    </div>
                  )
                })()}

                {/* Número e Data */}
                <div className="bg-white rounded-lg p-3 mb-3 shadow-md">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Número</p>
                      <p className="text-base font-bold text-gray-800">{pagamentoSelecionado.numeroComprovante}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500 uppercase">Data e Hora</p>
                      <p className="text-sm font-semibold text-gray-800">{new Date(pagamentoSelecionado.createdAt).toLocaleDateString("pt-BR")}</p>
                      <p className="text-xs text-gray-600">{new Date(pagamentoSelecionado.createdAt).toLocaleTimeString("pt-BR")}</p>
                    </div>
                  </div>
                </div>

                {/* Valor Destaque */}
                <div className="bg-gradient-to-r from-[#004161] to-[#005580] rounded-lg p-4 mb-3 text-center">
                  <p className="text-white text-sm uppercase mb-1">Valor da Transação</p>
                  <p className="text-white text-3xl font-bold">R$ {pagamentoSelecionado.valor}</p>
                </div>

                {/* Dados da Transação */}
                <div className="bg-white rounded-lg p-4 mb-3 shadow-md">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Pagador */}
                    <div>
                      <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                        <User className="w-5 h-5 text-[#004161]" />
                        PAGADOR
                      </h4>
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Nome</p>
                          <p className="text-sm font-semibold text-gray-800">{pagamentoSelecionado.nomePagador}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">CPF</p>
                          <p className="text-sm font-semibold text-gray-800">{pagamentoSelecionado.cpfPagador}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Referente a</p>
                          <p className="text-sm font-semibold text-gray-800">{pagamentoSelecionado.referenciaPagamento}</p>
                        </div>
                      </div>
                    </div>

                    {/* Recebedor */}
                    <div>
                      <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-[#004161]" />
                        RECEBEDOR
                      </h4>
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Beneficiário</p>
                          <p className="text-sm font-semibold text-gray-800">{dadosPrefeitura?.nomePrefeitura}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">CNPJ</p>
                          <p className="text-sm font-semibold text-gray-800">{dadosPrefeitura?.cnpj}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Chave PIX</p>
                          <p className="text-xs font-mono text-gray-800 break-all">{pagamentoSelecionado.chavePix}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* QR Code */}
                <div className="bg-white rounded-lg p-4 shadow-md">
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-700 mb-3 uppercase">QR Code PIX</p>
                    {qrCodeDataUrl && (
                      <div className="inline-block border-4 border-[#004161] rounded-lg p-2 bg-white">
                        <img src={qrCodeDataUrl} alt="QR Code PIX" className="w-48 h-48 mx-auto" />
                      </div>
                    )}
                    <p className="text-xs text-gray-600 mt-3">Escaneie o código acima com o app do seu banco</p>
                  </div>
                </div>
              </div>

              {/* Botões de Ação */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => handleImprimirNovaAba(pagamentoSelecionado)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#004161] text-white rounded-lg hover:bg-[#005580] transition-colors"
                >
                  <Printer className="w-5 h-5" />
                  Imprimir
                </button>
                <button
                  onClick={handleFecharComprovante}
                  className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}
